import React from "react";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import {
    ArrowRight,
    Calendar,
    Euro,
    FileText,
    Globe,
    Mail,
    MapPin,
    Phone,
    Plane,
    Sparkles,
    User,
    Users,
} from "lucide-react";

import { UserAvatar } from "@/components/ui/user-avatar";
import { formatBudget, formatTravelDates, formatCreatedDate } from "@/components/utils/utils";
import { useI18n } from '@/hooks/useI18n';
import { getCountryByCode } from '@/constants/countries';

// Tipo union per supportare entrambi i modelli
type TravelRequestType = DashboardTravelRequest | TravelRequest;

// Helper per normalizzare i valori tra i due tipi
const normalize = (tr: TravelRequestType) => ({
    contactFullName: 'contactFullName' in tr ? tr.contactFullName : `${tr.contact_first_name} ${tr.contact_last_name}`.trim(),
    contactEmail: 'contactEmail' in tr ? tr.contactEmail : tr.contact_email,
    contactPhone: 'contactPhone' in tr ? tr.contactPhone : tr.contact_phone,
    // Foto del contatto: solo il modello dashboard la porta (arriva dalla RPC). Il
    // modello request-trip non la espone, e li' resta il cerchio con le iniziali.
    contactAvatarUrl: 'contact' in tr ? tr.contact.avatarUrl ?? null : null,
    departurePoint: 'departurePoint' in tr ? tr.departurePoint : tr.departure_point,
    travelStartDate: 'travelStartDate' in tr ? tr.travelStartDate : tr.travel_start_date,
    travelEndDate: 'travelEndDate' in tr ? tr.travelEndDate : tr.travel_end_date,
    travelDatesFlexible: 'travelDatesFlexible' in tr ? tr.travelDatesFlexible : tr.travel_dates_flexible,
    totalGuests: 'totalGuests' in tr ? tr.totalGuests : tr.total_guests,
    guestsData: 'guestsData' in tr ? tr.guestsData : tr.guests_data,
    budgetMin: 'budgetMin' in tr ? tr.budgetMin : tr.budget_min,
    budgetMax: 'budgetMax' in tr ? tr.budgetMax : tr.budget_max,
    budgetFlexible: 'budgetFlexible' in tr ? tr.budgetFlexible : tr.budget_flexible,
    needsTransportation: 'needsTransportation' in tr ? tr.needsTransportation : tr.needs_transportation,
    additionalNotes: 'additionalNotes' in tr ? tr.additionalNotes : tr.additional_notes,
    createdAt: 'createdAt' in tr ? tr.createdAt : tr.created_at,
    updatedAt: 'updatedAt' in tr ? tr.updatedAt : tr.updated_at,
    // Provenienza dei viaggiatori (alpha-2 minuscolo, lo stesso `residency` che va a
    // RateHawk): si compila nella modale di modifica, quindi va letta anche qui.
    residency: tr.residency ?? null,
    // Per assignedAdvisor e preferredAdvisor - DashboardTravelRequest ha oggetti, TravelRequest ha users_detail
    assignedAdvisorName: 'assignedAdvisor' in tr && tr.assignedAdvisor 
        ? tr.assignedAdvisor.fullName 
        : ('users_detail' in tr && tr.users_detail?.assigned) ? `${tr.users_detail.assigned.first_name} ${tr.users_detail.assigned.last_name}` : null,
    preferredAdvisorName: 'preferredAdvisor' in tr && tr.preferredAdvisor 
        ? tr.preferredAdvisor.fullName 
        : ('users_detail' in tr && tr.users_detail?.preferred) ? `${tr.users_detail.preferred.first_name} ${tr.users_detail.preferred.last_name}` : null,
    assignedAdvisorEmail: 'assignedAdvisor' in tr && tr.assignedAdvisor
        ? tr.assignedAdvisor.email
        : ('users_detail' in tr && tr.users_detail?.assigned?.email) || null,
    assignedAdvisorAvatarUrl: 'assignedAdvisor' in tr && tr.assignedAdvisor
        ? tr.assignedAdvisor.avatarUrl ?? null
        : ('users_detail' in tr && tr.users_detail?.assigned?.avatar_url) || null,
    assignedAdvisorId: 'assignedAdvisorId' in tr ? tr.assignedAdvisorId : tr.assigned_designer_id,
    hasAssignedAdvisor: 'assignedAdvisor' in tr ? !!tr.assignedAdvisor : ('users_detail' in tr && !!tr.users_detail?.assigned),
    hasPreferredAdvisor: 'preferredAdvisor' in tr ? !!tr.preferredAdvisor : ('users_detail' in tr && !!tr.users_detail?.preferred),
});

/**
 * Una casella della striscia di riepilogo: etichetta minuta sopra, il dato in evidenza
 * sotto, e una riga di contorno facoltativa (la partenza, la flessibilita') in grigio.
 *
 * Le quattro caselle sono la risposta alle domande che si fanno tutti aprendo una
 * richiesta -- dove, quando, quanti, quanto -- e stanno su una riga sola: e' il motivo
 * per cui non sono card separate con bordo proprio.
 */
const SummaryTile: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    hint?: React.ReactNode;
}> = ({ icon, label, value, hint }) => (
    <div className="px-4 py-3 min-w-0">
        <div className="flex items-center gap-1.5 text-gray-400">
            {icon}
            <span className="text-[11px] uppercase tracking-wide font-medium">{label}</span>
        </div>
        <div className="mt-1 text-sm font-semibold text-gray-900 leading-snug break-words">{value}</div>
        {hint && <div className="mt-0.5 text-xs text-gray-500 leading-snug">{hint}</div>}
    </div>
);

/**
 * Card di una persona della richiesta. Cliente e travel advisor pesano uguale nella
 * lettura -- chi chiede e chi ha in carico -- quindi hanno la stessa card, affiancate:
 * avatar, nome, contatti, una riga di contorno e, dove c'e' davvero una scheda da
 * aprire, il bottone che ci porta. Senza `onClick` resta un div: niente elementi
 * focusabili che non fanno niente.
 */
const PersonCard: React.FC<{
    label: string;
    fullName: string | null;
    email?: string | null;
    phone?: string | null;
    avatarUrl?: string | null;
    /** Testo al posto del nome quando la persona non c'e' (advisor non assegnato). */
    placeholder?: string;
    meta?: React.ReactNode;
    onClick?: () => void;
    ctaLabel?: string;
}> = ({ label, fullName, email, phone, avatarUrl, placeholder, meta, onClick, ctaLabel }) => {
    const isClickable = !!onClick;

    const content = (
        <>
            {fullName ? (
                <UserAvatar
                    fullName={fullName}
                    email={email}
                    imageUrl={avatarUrl}
                    size="lg"
                />
            ) : (
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-dashed border-gray-300 bg-gray-50 text-gray-400">
                    <User className="h-5 w-5" />
                </span>
            )}
            <div className="min-w-0 flex-1 text-left">
                <div className="text-[11px] uppercase tracking-wide font-medium text-gray-400">
                    {label}
                </div>
                <div
                    className={`text-base font-semibold truncate ${fullName ? 'text-gray-900' : 'text-gray-400 font-medium'}`}
                >
                    {fullName ?? placeholder}
                </div>
                {(email || phone) && (
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                        {email && (
                            <span className="inline-flex items-center gap-1.5 min-w-0">
                                <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                <span className="truncate">{email}</span>
                            </span>
                        )}
                        {phone && (
                            <span className="inline-flex items-center gap-1.5">
                                <Phone className="h-3.5 w-3.5 text-gray-400" />
                                {phone}
                            </span>
                        )}
                    </div>
                )}
                {meta && <div className="mt-1 text-xs text-gray-400">{meta}</div>}
            </div>
            {isClickable && ctaLabel && (
                <span className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 flex-shrink-0">
                    {ctaLabel}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
            )}
        </>
    );

    const shell = "w-full flex items-center gap-3 rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 to-white px-4 py-3 shadow-sm";

    return isClickable ? (
        <button
            type="button"
            onClick={onClick}
            title={ctaLabel}
            className={`group ${shell} text-left transition-all hover:border-orange-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2`}
        >
            {content}
        </button>
    ) : (
        <div className={shell}>{content}</div>
    );
};

/** Riga secondaria a pillola: informazioni di contorno, mai il dato principale. */
const InfoChip: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700">
        {icon}
        {children}
    </span>
);

interface TravelRequestDetailsProps {
    travelRequest: TravelRequestType;
    /**
     * Apre la scheda del cliente. Se assente la card del contatto resta statica: la
     * scheda e' riservata a chi puo' vederla (agency, admin, advisor), e la stessa
     * modale la mostra anche al viaggiatore nella proposta condivisa.
     */
    onClientClick?: () => void;
    /**
     * Apre la scheda del consulente assegnato. Come per il cliente: assente dove la
     * scheda non e' consultabile (proposta condivisa, traveler).
     */
    onAdvisorClick?: () => void;
}

export const TravelRequestDetails: React.FC<TravelRequestDetailsProps> = ({
    travelRequest,
    onClientClick,
    onAdvisorClick,
}) => {
    const { t } = useI18n();
    const data = normalize(travelRequest);
    
    // Notti: lo stesso conteggio che la modale di modifica mostra accanto alle date.
    const nights = (() => {
        if (!data.travelStartDate || !data.travelEndDate) return null;
        const ms = new Date(data.travelEndDate).getTime() - new Date(data.travelStartDate).getTime();
        const n = Math.round(ms / (1000 * 60 * 60 * 24));
        return n > 0 ? n : null;
    })();

    const residencyCountry = data.residency
        ? getCountryByCode(data.residency.toUpperCase())
        : undefined;


    // Riga di contorno del cliente: quando e' arrivata la richiesta e, se toccata,
    // quando e' stata aggiornata.
    const clientMeta = (
        <>
            {t('travelRequest.requestDate')}: {formatCreatedDate(data.createdAt, { format: 'long' })}
            {data.updatedAt && data.updatedAt !== data.createdAt && (
                <> · {t('travelRequest.updatedOn')} {formatCreatedDate(data.updatedAt, { format: 'long' })}</>
            )}
        </>
    );

    // Riga di contorno dell'advisor: quando nessuno ha ancora preso in carico la
    // richiesta, l'unica cosa da dire e' se il cliente ne aveva indicato uno.
    const advisorMeta = !data.hasAssignedAdvisor && data.hasPreferredAdvisor
        ? `${t('travelRequest.preferred')}: ${data.preferredAdvisorName}`
        : undefined;

    return (
        <div className="py-4 px-6 space-y-3">

            {/* Riepilogo: dove, quando, quanti, quanto. E' la prima cosa che si cerca
                aprendo una richiesta, quindi apre la lettura; una riga sola su desktop,
                due colonne su mobile, separatori al posto di quattro bordi. */}
            <div className="rounded-xl border border-gray-200 bg-white grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-gray-100 overflow-hidden">
                <SummaryTile
                    icon={<MapPin className="h-3.5 w-3.5" />}
                    label={t('itinerary.destinations')}
                    value={
                        travelRequest.destinations.length > 0
                            ? travelRequest.destinations.join(", ")
                            : t('travelRequest.destinationTBD')
                    }
                    hint={
                        data.departurePoint
                            ? `${t('travelRequest.departureFrom')}: ${data.departurePoint}`
                            : undefined
                    }
                />
                <SummaryTile
                    icon={<Calendar className="h-3.5 w-3.5" />}
                    label={t('travelRequest.travelDates')}
                    value={formatTravelDates(data.travelStartDate, data.travelEndDate, {
                        fallbackText: t('travelRequest.datesToBeDefined'),
                        showYear: true,
                    })}
                    hint={
                        [
                            nights ? String(t('clientRequest.trip.nights', { count: nights })) : null,
                            data.travelDatesFlexible ? String(t('travelRequest.flexibleDates')) : null,
                        ]
                            .filter(Boolean)
                            .join(' · ') || undefined
                    }
                />
                <SummaryTile
                    icon={<Users className="h-3.5 w-3.5" />}
                    label={t('travelRequest.group')}
                    value={`${data.totalGuests} ${data.totalGuests === 1 ? t('travelRequest.person') : t('travelRequest.people')}`}
                    hint={
                        data.guestsData && data.guestsData.length > 0
                            ? `${t('travelRequest.guestsDetails')}: ${data.guestsData.map((guest) => guest.age).join(", ")} ${t('travelRequest.years')}`
                            : undefined
                    }
                />
                <SummaryTile
                    icon={<Euro className="h-3.5 w-3.5" />}
                    label={t('travelRequest.budget')}
                    value={formatBudget(data.budgetMin, data.budgetMax)}
                    hint={data.budgetFlexible ? t('travelRequest.flexibleBudget') : undefined}
                />
            </div>

            {/* Le due persone della richiesta, con lo stesso peso: chi la fa e chi la
                prende in carico. Una card per riga, a tutta larghezza. */}
            <div className="space-y-3">
                <PersonCard
                    label={t('travelRequest.client')}
                    fullName={data.contactFullName}
                    email={data.contactEmail}
                    phone={data.contactPhone}
                    avatarUrl={data.contactAvatarUrl}
                    meta={clientMeta}
                    onClick={onClientClick}
                    ctaLabel={t('travelRequest.openClientSheet')}
                />
                <PersonCard
                    label={t('travelRequest.designer')}
                    fullName={data.hasAssignedAdvisor ? data.assignedAdvisorName : null}
                    email={data.hasAssignedAdvisor ? data.assignedAdvisorEmail : null}
                    avatarUrl={data.assignedAdvisorAvatarUrl}
                    placeholder={t('travelRequest.notAssigned')}
                    meta={advisorMeta}
                    onClick={data.hasAssignedAdvisor && data.assignedAdvisorId ? onAdvisorClick : undefined}
                    ctaLabel={t('travelRequest.openAdvisorSheet')}
                />
            </div>

            {/* Interessi - Solo se presenti */}
            {travelRequest.interests && travelRequest.interests.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 space-y-2">
                    <div className="flex items-center gap-1.5 text-gray-400">
                        <Sparkles className="h-3.5 w-3.5" />
                        <span className="text-[11px] uppercase tracking-wide font-medium">
                            {t('travelRequest.interests')}
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {travelRequest.interests.map((interest, index) => (
                            <span
                                key={index}
                                className="px-2.5 py-0.5 bg-gray-100 text-gray-800 rounded-full text-xs"
                            >
                                {interest}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Note aggiuntive - Solo se presenti */}
            {data.additionalNotes && (
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 space-y-1.5">
                    <div className="flex items-center gap-1.5 text-gray-400">
                        <FileText className="h-3.5 w-3.5" />
                        <span className="text-[11px] uppercase tracking-wide font-medium">
                            {t('travelRequest.additionalNotes')}
                        </span>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm whitespace-pre-line">{data.additionalNotes}</p>
                </div>
            )}

            {/* Coda: trasporto e provenienza. Sono risposte brevi di contorno, non il
                motivo per cui si apre la richiesta: stanno in fondo, non fra le card. */}
            <div className="flex flex-wrap items-center gap-2">
                <InfoChip icon={<Plane className="h-3.5 w-3.5 text-gray-400" />}>
                    {data.needsTransportation
                        ? t('travelRequest.transportationRequired')
                        : t('travelRequest.transportationNotRequired')}
                </InfoChip>

                {residencyCountry && (
                    <InfoChip icon={<Globe className="h-3.5 w-3.5 text-gray-400" />}>
                        <span className="text-gray-400">{t('clientRequest.travellers.residency')}:</span>
                        {residencyCountry.flag} {residencyCountry.name}
                    </InfoChip>
                )}
            </div>
        </div>
    );
};
